import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TechnicalSpecsSection = () => {
  const [activeCategory, setActiveCategory] = useState('performance');

  const specifications = {
    performance: {
      title: 'Performance & Scalability',
      icon: 'Zap',
      specs: [
        {
          metric: 'Message Latency',
          value: '< 50ms',
          description: 'Global average message delivery time',
          icon: 'Clock'
        },
        {
          metric: 'Concurrent Connections',
          value: '1M+',
          description: 'Maximum simultaneous connections per instance',
          icon: 'Users'
        },
        {
          metric: 'Messages per Second',
          value: '100K+',
          description: 'Peak message throughput capacity',
          icon: 'Send'
        },
        {
          metric: 'Auto-scaling',
          value: '< 30s',
          description: 'Time to scale up/down based on demand',
          icon: 'TrendingUp'
        },
        {
          metric: 'API Response Time',
          value: '< 100ms',
          description: 'Average REST API response time',
          icon: 'Gauge'
        },
        {
          metric: 'WebSocket Reconnection',
          value: '< 5s',
          description: 'Automatic reconnection time on failure',
          icon: 'RefreshCw'
        }
      ]
    },
    security: {
      title: 'Security & Compliance',
      icon: 'Shield',
      specs: [
        {
          metric: 'Encryption',
          value: 'AES-256',
          description: 'End-to-end message encryption standard',
          icon: 'Lock'
        },
        {
          metric: 'TLS Version',
          value: 'TLS 1.3',
          description: 'Transport layer security protocol',
          icon: 'Shield'
        },
        {
          metric: 'SOC 2 Compliance',
          value: 'Type II',
          description: 'Security, availability, and confidentiality',
          icon: 'CheckCircle'
        },
        {
          metric: 'GDPR Compliance',
          value: 'Certified',
          description: 'European data protection regulation',
          icon: 'FileCheck'
        },
        {
          metric: 'HIPAA Compliance',
          value: 'Available',
          description: 'Healthcare data protection (Enterprise)',
          icon: 'Heart'
        },
        {
          metric: 'Data Retention',
          value: 'Configurable',
          description: 'Customizable data retention policies',
          icon: 'Database'
        }
      ]
    },
    infrastructure: {
      title: 'Infrastructure & Reliability',
      icon: 'Server',
      specs: [
        {
          metric: 'Uptime SLA',
          value: '99.9%',
          description: 'Guaranteed service availability',
          icon: 'Activity'
        },
        {
          metric: 'Global Regions',
          value: '15+',
          description: 'Worldwide data center locations',
          icon: 'Globe'
        },
        {
          metric: 'CDN Edge Locations',
          value: '200+',
          description: 'Content delivery network endpoints',
          icon: 'MapPin'
        },
        {
          metric: 'Load Balancing',
          value: 'Multi-tier',
          description: 'Intelligent traffic distribution',
          icon: 'BarChart3'
        },
        {
          metric: 'Backup & Recovery',
          value: '< 1 hour',
          description: 'Maximum recovery time objective',
          icon: 'HardDrive'
        },
        {
          metric: 'DDoS Protection',
          value: 'Enterprise',
          description: 'Advanced threat mitigation',
          icon: 'ShieldCheck'
        }
      ]
    },
    apis: {
      title: 'APIs & Integration',
      icon: 'Code',
      specs: [
        {
          metric: 'REST API Version',
          value: 'v2.0',
          description: 'Current stable API version',
          icon: 'Code'
        },
        {
          metric: 'WebSocket Protocol',
          value: 'RFC 6455',
          description: 'Standard WebSocket implementation',
          icon: 'Zap'
        },
        {
          metric: 'SDK Languages',
          value: '10+',
          description: 'Official software development kits',
          icon: 'Package'
        },
        {
          metric: 'Webhook Support',
          value: 'Full',
          description: 'Real-time event notifications',
          icon: 'Webhook'
        },
        {
          metric: 'Rate Limiting',
          value: 'Configurable',
          description: 'Customizable API rate limits',
          icon: 'Timer'
        },
        {
          metric: 'OpenAPI Spec',
          value: '3.0',
          description: 'Complete API documentation standard',
          icon: 'FileText'
        }
      ]
    }
  };

  const categories = [
    { id: 'performance', label: 'Performance', icon: 'Zap' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'infrastructure', label: 'Infrastructure', icon: 'Server' },
    { id: 'apis', label: 'APIs', icon: 'Code' }
  ];

  const certifications = [
    {
      name: 'SOC 2 Type II',
      description: 'Security, Availability, Processing Integrity',
      icon: 'Shield',
      status: 'Certified'
    },
    {
      name: 'ISO 27001',
      description: 'Information Security Management',
      icon: 'Lock',
      status: 'Certified'
    },
    {
      name: 'GDPR',
      description: 'General Data Protection Regulation',
      icon: 'FileCheck',
      status: 'Compliant'
    },
    {
      name: 'HIPAA',
      description: 'Healthcare Information Portability',
      icon: 'Heart',
      status: 'Available'
    }
  ];

  const currentCategory = specifications?.[activeCategory];

  return (
    <section className="py-24 bg-card/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-warning/10 border border-warning/20 rounded-full px-4 py-2 mb-6">
            <Icon name="Settings" size={16} className="text-warning" />
            <span className="text-sm font-medium text-warning">
              Technical Specifications
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Enterprise-grade
            <span className="block text-warning">infrastructure</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Built on battle-tested infrastructure with industry-leading performance, 
            security, and reliability standards.
          </p>
        </div>

        {/* Category Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setActiveCategory(category?.id)}
              className={`inline-flex items-center space-x-2 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeCategory === category?.id
                  ? 'bg-primary text-primary-foreground shadow-elevation-2'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <Icon name={category?.icon} size={18} />
              <span>{category?.label}</span>
            </button>
          ))}
        </div>

        {/* Specifications Grid */}
        <div className="mb-16">
          <div className="flex items-center space-x-3 mb-8">
            <Icon name={currentCategory?.icon} size={24} className="text-primary" />
            <h3 className="text-2xl font-bold text-foreground">
              {currentCategory?.title}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentCategory?.specs?.map((spec, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 hover:border-primary/30 transition-all duration-300 hover:shadow-elevation-2"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name={spec?.icon} size={20} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-foreground text-sm">
                        {spec?.metric}
                      </h4>
                      <span className="text-2xl font-bold text-primary">
                        {spec?.value}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {spec?.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            Security Certifications & Compliance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications?.map((cert, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center hover:border-accent/30 transition-all duration-300"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mx-auto mb-4">
                  <Icon name={cert?.icon} size={24} className="text-accent" />
                </div>
                <h4 className="font-semibold text-foreground mb-2">
                  {cert?.name}
                </h4>
                <p className="text-sm text-muted-foreground mb-3">
                  {cert?.description}
                </p>
                <span className="inline-flex items-center px-3 py-1 bg-accent/10 text-accent rounded-full text-xs font-medium">
                  {cert?.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Architecture Diagram */}
        <div className="bg-card border border-border rounded-xl p-8">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            Global Infrastructure Architecture
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            {/* Client Layer */}
            <div className="text-center">
              <div className="bg-primary/10 rounded-xl p-6 mb-4">
                <Icon name="Smartphone" size={48} className="text-primary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Client Applications</h4>
                <p className="text-sm text-muted-foreground">
                  Web, Mobile, Desktop apps connect via WebSocket or REST APIs
                </p>
              </div>
              <div className="flex justify-center">
                <Icon name="ArrowRight" size={24} className="text-muted-foreground" />
              </div>
            </div>

            {/* Edge Layer */}
            <div className="text-center">
              <div className="bg-secondary/10 rounded-xl p-6 mb-4">
                <Icon name="Globe" size={48} className="text-secondary mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Global Edge Network</h4>
                <p className="text-sm text-muted-foreground">
                  200+ CDN locations with intelligent routing and load balancing
                </p>
              </div>
              <div className="flex justify-center">
                <Icon name="ArrowRight" size={24} className="text-muted-foreground" />
              </div>
            </div>

            {/* Core Layer */}
            <div className="text-center">
              <div className="bg-accent/10 rounded-xl p-6 mb-4">
                <Icon name="Server" size={48} className="text-accent mx-auto mb-4" />
                <h4 className="font-semibold text-foreground mb-2">Core Infrastructure</h4>
                <p className="text-sm text-muted-foreground">
                  Auto-scaling message processing with redundancy and failover
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => window.location.href = '/api-documentation'}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
            >
              <Icon name="FileText" size={18} />
              <span>View Technical Documentation</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnicalSpecsSection;